import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { chatApi } from '@/api/chatApi';
// import chatReducer from '@/features/chat/chatSlice';
import chatReducer, { resetChat } from '../features/chat/chatSlice';
import type { ChatState } from '@/types';

const CHAT_PERSIST_KEY = 'chat_state_v1';

function loadPreloadedChatState(): ChatState | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const raw = window.localStorage.getItem(CHAT_PERSIST_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw) as ChatState;
  } catch {
    return undefined;
  }
}

function pruneChatState(state: any): any {
  if (!state || typeof state !== 'object') return state;
  const conversations = Array.isArray(state.conversations) ? state.conversations : [];
  const prunedConversations = conversations.slice(0, 30).map((c: any) => ({
    ...c,
    messages: Array.isArray(c.messages) ? c.messages.slice(-200) : [],
  }));
  return {
    ...state,
    conversations: prunedConversations,
    messages: Array.isArray(state.messages) ? state.messages.slice(-200) : [],
  };
}

const rootReducer = combineReducers({
  [chatApi.reducerPath]: chatApi.reducer,
  chat: chatReducer,
});

const preloadedChat = loadPreloadedChatState();

export const store = configureStore({
  reducer: rootReducer,
  preloadedState: preloadedChat ? { chat: preloadedChat } : undefined,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false, // This turns off the warning entirely
    }).concat(chatApi.middleware),
  devTools: import.meta.env.MODE !== 'production',
});

// Conversations are retained as history, but reloading must never resume the
// previously active chat/session. The first message in this fresh conversation
// receives a new backend session ID.
if (preloadedChat) {
  store.dispatch(resetChat());
}

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

if (typeof window !== 'undefined') {
  let last = '';
  store.subscribe(() => {
    try {
      const next = JSON.stringify(pruneChatState(store.getState().chat));
      if (next === last) return;
      last = next;
      window.localStorage.setItem(CHAT_PERSIST_KEY, next);
    } catch {
      // ignore persistence errors
    }
  });
}
